import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Pagination, { usePagination } from "@/components/Pagination";
import {
  RefreshCw, FileSpreadsheet, FileText, MessageSquare, PhoneCall,
  History, CalendarClock, Plus, ClipboardCheck, X,
} from "lucide-react";
import { toast } from "sonner";
import { formatBill } from "@/lib/format";
import WhatsAppMessageModal, { buildCollectionReadyMessage } from "@/components/WhatsAppMessageModal";
import ImportButton from "@/components/ImportButton";
import { useAuth } from "@/context/AuthContext";

const inr = (n) => "Rs. " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const daysAhead = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const RANGES = [
  { key: "30d", label: "Next 30 Days", from: today(), to: daysAhead(30) },
  { key: "90d", label: "Next 90 Days", from: today(), to: daysAhead(90) },
  { key: "past30", label: "Past 30 Days", from: daysAgo(30), to: today() },
  { key: "past90", label: "Past 90 Days", from: daysAgo(90), to: today() },
  { key: "all", label: "All", from: "", to: "" },
  { key: "custom", label: "Custom", from: "", to: "" },
];

export default function FollowUp() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending");
  const [rangeKey, setRangeKey] = useState("90d");
  const [dateFrom, setDateFrom] = useState(daysAhead(-30));
  const [dateTo, setDateTo] = useState(daysAhead(90));
  const [historyRow, setHistoryRow] = useState(null);
  const [addNoteRow, setAddNoteRow] = useState(null);
  const [waRow, setWaRow] = useState(null);

  const paginationKey = `${status}|${rangeKey}|${dateFrom}|${dateTo}`;
  const { page, setPage, pageRows, totalPages, total, pageSize, setPageSize } = usePagination(rows, 50, paginationKey);

  useEffect(() => {
    const r = RANGES.find((x) => x.key === rangeKey);
    if (r && rangeKey !== "custom") { setDateFrom(r.from); setDateTo(r.to); }
  }, [rangeKey]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/follow-ups", {
        params: { status, date_from: dateFrom || undefined, date_to: dateTo || undefined },
      });
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, rangeKey, dateFrom, dateTo]);

  const downloadFile = async (format) => {
    if (rows.length === 0) return toast.info("Nothing to export");
    const path = format === "pdf" ? "/follow-ups/export-pdf" : "/follow-ups/export";
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const mime = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    try {
      const res = await api.get(path, {
        params: { status, date_from: dateFrom || undefined, date_to: dateTo || undefined },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `follow-ups-${status}-${today()}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Follow-ups ${format.toUpperCase()} downloaded`);
    } catch { toast.error(`Failed to download ${format.toUpperCase()}`); }
  };

  const totalBalance = useMemo(() => rows.reduce((s, r) => s + Number(r.balance || 0), 0), [rows]);

  const openWhatsApp = (row) => {
    if (!row.mobile) return toast.error("No mobile on record");
    setWaRow(row);
  };

  return (
    <div className="space-y-4" data-testid="followup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight text-slate-900">Follow-Up</h1>
          <p className="text-xs text-slate-500 mt-0.5">Customer follow-up log with history of calls & promised payment dates.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => downloadFile("xlsx")} className="h-9 bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white font-semibold" data-testid="followup-excel-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          <Button onClick={() => downloadFile("pdf")} className="h-9 bg-red-500 hover:bg-red-600 text-white font-semibold" data-testid="followup-pdf-btn">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
          {isAdmin && (
            <Button
              onClick={async () => {
                try {
                  const res = await api.get(`/follow-ups/export?all=true`, { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement("a");
                  a.href = url; a.download = `follow-ups-ALL.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Downloaded ALL follow-ups");
                } catch { toast.error("Failed to download ALL"); }
              }}
              className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
              data-testid="followup-excel-all-btn"
              title="Admin: download every follow-up in one click (ignores filters)"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export ALL
            </Button>
          )}
          {isAdmin && <ImportButton resource="follow-ups" onDone={load} label="Import Excel" />}
          <Button onClick={load} disabled={loading} className="h-9 bg-[#2E77E6] hover:bg-[#2361C4] text-white font-semibold" data-testid="followup-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Status</Label>
          <div className="flex gap-1">
            {["pending", "closed", "all"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} className={`text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-md border transition ${status === s ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`} data-testid={`fu-status-${s}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Range</Label>
          <div className="flex gap-1 flex-wrap">
            {RANGES.map((r) => (
              <button key={r.key} onClick={() => setRangeKey(r.key)} className={`text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-md border transition ${rangeKey === r.key ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`} data-testid={`fu-range-${r.key}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {rangeKey === "custom" && (
          <>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="fu-date-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="fu-date-to" />
            </div>
          </>
        )}
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total Balance</div>
          <div className="text-lg font-mono-num font-black text-red-600" data-testid="fu-total-balance">{inr(totalBalance)}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm crm-table" data-testid="followup-table">
            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
                <th className="px-3 py-2.5 text-left font-semibold">Mobile</th>
                <th className="px-3 py-2.5 text-left font-semibold">Doc No</th>
                <th className="px-3 py-2.5 text-left font-semibold">Service</th>
                <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                <th className="px-3 py-2.5 text-left font-semibold">Follow-Up</th>
                <th className="px-3 py-2.5 text-left font-semibold">Last Note</th>
                <th className="px-3 py-2.5 text-left font-semibold">Status</th>
                <th className="px-3 py-2.5 text-right font-semibold w-56">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageRows.length === 0 ? (
                <tr><td colSpan={9} className="py-16 text-center text-slate-400 text-sm">No follow-ups in this range</td></tr>
              ) : pageRows.map((r) => (
                <tr key={r.id} data-testid={`fu-row-${r.id}`} className={r.status === "closed" ? "opacity-60" : ""}>
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.customer_name}</td>
                  <td className="px-3 py-2 font-mono-num text-slate-700">{r.mobile}</td>
                  <td className="px-3 py-2 font-mono-num text-slate-600">{formatBill(r.doc_no)}</td>
                  <td className="px-3 py-2 text-slate-700 text-xs">{r.service_name}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-bold text-red-600">{inr(r.balance)}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded text-amber-800 font-semibold">
                      <CalendarClock className="h-3 w-3" />{r.follow_up_date}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-xs max-w-xs truncate" title={r.last_note}>{r.last_note || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                      r.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => openWhatsApp(r)} className="p-1.5 rounded hover:bg-emerald-50 text-[#25D366]" title="WhatsApp reminder"><MessageSquare className="h-3.5 w-3.5" /></button>
                      <a href={`tel:${r.mobile}`} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" title="Call"><PhoneCall className="h-3.5 w-3.5" /></a>
                      <button onClick={() => setHistoryRow(r)} className="p-1.5 rounded hover:bg-slate-100 text-slate-700" title="View history" data-testid={`fu-history-${r.id}`}><History className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setAddNoteRow(r)} className="ml-1 px-2 py-1 rounded bg-[color:var(--brand-navy)] hover:bg-slate-800 text-white text-[10px] font-bold uppercase" data-testid={`fu-add-note-${r.id}`}>+ Note</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} setPageSize={setPageSize} testid="followup-pagination" />
      </div>

      {historyRow && (
        <HistoryDialog row={historyRow} onClose={() => setHistoryRow(null)} />
      )}
      {addNoteRow && (
        <AddNoteDialog
          row={addNoteRow}
          onClose={() => setAddNoteRow(null)}
          onSaved={() => { setAddNoteRow(null); load(); toast.success("Follow-up note saved"); }}
        />
      )}
      {waRow && (
        <WhatsAppMessageModal
          open={!!waRow}
          onOpenChange={(v) => { if (!v) setWaRow(null); }}
          mobile={waRow.mobile}
          customerName={waRow.customer_name}
          subtitle={`Doc ${formatBill(waRow.doc_no)}`}
          defaultMessage={buildCollectionReadyMessage(waRow.customer_name)}
          title="WhatsApp Follow-Up"
        />
      )}
    </div>
  );
}

function HistoryDialog({ row, onClose }) {
  const history = (row.history || []).slice().reverse();
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="fu-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <History className="h-5 w-5 text-[color:var(--brand-navy)]" />
            Follow-Up History — {row.customer_name}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 space-y-1 mb-3">
          <div><b>Doc No:</b> {row.doc_no} • <b>Mobile:</b> {row.mobile} • <b>Balance:</b> <span className="text-red-600 font-bold">{inr(row.balance)}</span></div>
        </div>
        <div className="max-h-96 overflow-y-auto space-y-2">
          {history.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">No history yet</div>}
          {history.map((h, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-3 bg-slate-50">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                <span>Recorded: {(h.date || "").slice(0, 16).replace("T", " ")}</span>
                <span>Promised: <span className="text-amber-700">{h.follow_up_date}</span></span>
              </div>
              <div className="text-sm text-slate-800">{h.note || <span className="italic text-slate-400">(no note)</span>}</div>
              <div className="text-[10px] text-slate-500 mt-1">by {h.by || "?"}</div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline" data-testid="fu-history-close"><X className="h-4 w-4 mr-1" /> Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddNoteDialog({ row, onClose, onSaved }) {
  const [date, setDate] = useState(row.follow_up_date || daysAhead(3));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!date) return toast.error("Please pick a follow-up date");
    if (!note.trim()) return toast.error("Please enter what the customer said");
    setSaving(true);
    try {
      await api.post("/follow-ups/upsert", { receipt_id: row.receipt_id || row.id, follow_up_date: date, note });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="fu-add-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Plus className="h-5 w-5 text-[color:var(--brand-teal)]" />
            Add Follow-Up Note — {row.customer_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            <b>Doc No:</b> {row.doc_no} • <b>Balance:</b> <span className="text-red-600 font-bold">{inr(row.balance)}</span>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Next Follow-Up Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9" data-testid="fu-note-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">What did the customer say?</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder='e.g. "Will pay by 15th, salary credited on 10th…"'
              className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-navy)]"
              data-testid="fu-note-text"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline" data-testid="fu-note-cancel"><X className="h-4 w-4 mr-1" /> Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white font-semibold" data-testid="fu-note-save">
            <ClipboardCheck className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save Follow-Up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
